import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
    loginWithCredentials,
    trySsoLogin,
    refreshTokens,
    logout as oauthLogout,
    exchangeCodeForTokens
} from './oauthClient';
import { parseJwt } from './jwt';
import { getStoredOAuthTokens, OAUTH_TOKENS_KEY } from './tokenStorage';
import { AUTH_SESSION_EXPIRED_EVENT } from '../utils/adminUi';
import config from '../config';

const AuthContext = createContext(null);

// ─── Smart Session Config ────────────────────────────────────
const INACTIVITY_TIMEOUT_MS = config.AUTH_INACTIVITY_TIMEOUT_MS;
const CHECK_INTERVAL_MS = config.AUTH_CHECK_INTERVAL_MS;
const REFRESH_BEFORE_MS = config.AUTH_REFRESH_BEFORE_MS;
const SHOULD_LOG_AUTH_DEBUG = config.ENABLE_LOGGING && config.ENVIRONMENT === 'development' && process.env.NODE_ENV !== 'test';

// Events that count as "user is active"
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export function AuthProvider({ children }) {
    // Initialize tokens from localStorage so the session survives page reloads
    const [tokens, setTokens] = useState(() => {
        return getStoredOAuthTokens();
    });
    const [loading, setLoading] = useState(true);
    const lastActivityRef = useRef(Date.now());
    const refreshingRef = useRef(false); // Prevent duplicate refresh calls
    const ssoCalledRef = useRef(false);  // Prevent double SSO call (React StrictMode)
    const loggingOutRef = useRef(false); // Prevent duplicate concurrent logouts

    // ─── Track user activity ─────────────────────────────────
    const updateActivity = useCallback(() => {
        lastActivityRef.current = Date.now();
    }, []);

    // ─── Multi-tab Storage Synchronization ───────────────────
    useEffect(() => {
        const onStorage = (event) => {
            if (event.key === OAUTH_TOKENS_KEY) {
                setTokens(getStoredOAuthTokens());
            }
        };

        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener('storage', onStorage);
        };
    }, []);

    // ─── Listen for Session Expired Events (401s from APIs) ──
    useEffect(() => {
        let mounted = true;

        const handleSessionExpired = async () => {
            if (loggingOutRef.current) return;
            loggingOutRef.current = true;

            try {
                if (SHOULD_LOG_AUTH_DEBUG) {
                    console.log('[Auth] Session expired event received, logging out');
                }
                await oauthLogout();
                if (mounted) {
                    setTokens(null);
                }
            } catch (err) {
                if (SHOULD_LOG_AUTH_DEBUG) {
                    console.warn('[Auth] Session expired logout failed:', err);
                }
                if (mounted) {
                    setTokens(null);
                }
            } finally {
                loggingOutRef.current = false;
            }
        };

        window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
        return () => {
            mounted = false;
            window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
        };
    }, []);

    // ─── SSO on startup ──────────────────────────────────────
    useEffect(() => {
        // Guard: React 18 StrictMode double-mounts in dev — only call SSO once
        if (ssoCalledRef.current) return;
        ssoCalledRef.current = true;

        // If we already have tokens from localStorage, skip the SSO network call
        if (tokens) {
            setLoading(false);
            return;
        }

        let mounted = true;

        (async () => {
            try {
                const ssoTokens = await trySsoLogin();
                if (mounted && ssoTokens) setTokens(ssoTokens);
            } finally {
                if (mounted) setLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── Smart Auto-Refresh & Inactivity Logout ──────────────
    useEffect(() => {
        if (!tokens?.access_token) return;

        // Listen for user activity
        ACTIVITY_EVENTS.forEach(event =>
            window.addEventListener(event, updateActivity, { passive: true })
        );

        const intervalId = setInterval(async () => {
            const now = Date.now();
            const inactiveMs = now - lastActivityRef.current;

            // ① INACTIVITY CHECK — if user idle for X min, logout immediately
            //    This check runs regardless of token state to prevent infinite sessions.
            if (inactiveMs >= INACTIVITY_TIMEOUT_MS) {
                if (SHOULD_LOG_AUTH_DEBUG) {
                    console.log('[Auth] Inactivity threshold reached, logging out');
                }
                await oauthLogout();
                setTokens(null);
                return;
            }

            // ② TOKEN EXPIRY CHECK — decode token to see time left
            const decoded = parseJwt(tokens.access_token);
            if (!decoded?.exp) return;

            const expiresAt = decoded.exp * 1000;
            const timeUntilExpiry = expiresAt - now;

            // Token still has plenty of time — do nothing
            if (timeUntilExpiry > REFRESH_BEFORE_MS) return;

            // ③ TOKEN EXPIRING SOON — user IS active (passed check ①)
            //    Refresh silently. Guard against duplicate calls.
            if (refreshingRef.current) return;
            refreshingRef.current = true;

            try {
                if (SHOULD_LOG_AUTH_DEBUG) {
                    console.log('[Auth] Token expiring in', Math.round(timeUntilExpiry / 1000), 'sec, refreshing');
                }
                const refreshed = await refreshTokens();
                setTokens(refreshed);
                if (SHOULD_LOG_AUTH_DEBUG) {
                    console.log('[Auth] Token refreshed');
                }
            } catch (e) {
                // ④ REFRESH FAILED — force logout (refresh token expired or revoked)
                if (SHOULD_LOG_AUTH_DEBUG) {
                    console.error('[Auth] Refresh failed, logging out', e.message);
                }
                await oauthLogout();
                setTokens(null);
            } finally {
                refreshingRef.current = false;
            }
        }, CHECK_INTERVAL_MS);

        return () => {
            clearInterval(intervalId);
            ACTIVITY_EVENTS.forEach(event =>
                window.removeEventListener(event, updateActivity)
            );
        };
    }, [tokens, updateActivity]);

    // ─── Context value ───────────────────────────────────────
    const value = useMemo(() => ({
        tokens,
        isAuthenticated: !!tokens?.access_token,
        loading,
        login: async (email, password) => {
            const issued = await loginWithCredentials(email, password);
            setTokens(issued);
            lastActivityRef.current = Date.now();
            return issued;
        },
        refresh: async () => {
            const refreshed = await refreshTokens();
            setTokens(refreshed);
            return refreshed;
        },
        logout: async () => {
            await oauthLogout();
            setTokens(null);
        },
        handleCallback: async (code) => {
            const issued = await exchangeCodeForTokens(code);
            setTokens(issued);
            lastActivityRef.current = Date.now();
            return issued;
        }
    }), [tokens, loading]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
    return ctx;
}
